/**
 * Static fallback cash-purchase discount (%).
 * Used only when the backend setting has not yet loaded or fails to load.
 * Once the backend /api/ecommerce/settings endpoint exists, the live value
 * from the API will take precedence everywhere via DiscountContext.
 */
export const CASH_DISCOUNT_PERCENT = 5;

/**
 * Resolves the effective cash discount percentage for a product.
 * Prioritizes product-specific discount fields:
 *   - product.instant_cash_discount
 *   - product.instantCashDiscount
 *   - product.cash_discount_percent
 *   - product.cashDiscountPercent
 *
 * If a valid number (>= 0 and <= 100) is set on the product, that value is used.
 * If not set (null / undefined / empty string), it falls back to:
 *   - fallbackDiscount (e.g. store-wide setting from DiscountContext)
 *   - or CASH_DISCOUNT_PERCENT (default 5%)
 *
 * @param {object} product             - Product object or cart/wishlist item
 * @param {number} [fallbackDiscount]  - Fallback discount percentage
 * @returns {number}                   - Resolved discount percentage (0 to 100)
 */
export function getProductDiscount(product, fallbackDiscount = CASH_DISCOUNT_PERCENT) {
    if (product && typeof product === 'object') {
        const candidateKeys = [
            'instant_cash_discount',
            'instantCashDiscount',
            'cash_discount_percent',
            'cashDiscountPercent',
        ];
        for (const key of candidateKeys) {
            const val = product[key];
            if (val !== undefined && val !== null && val !== '') {
                const num = Number(val);
                if (Number.isFinite(num) && num >= 0 && num <= 100) {
                    return num;
                }
            }
        }
    }
    const fb = Number(fallbackDiscount);
    return Number.isFinite(fb) && fb >= 0 && fb <= 100 ? fb : CASH_DISCOUNT_PERCENT;
}

/**
 * Unit cash price after discountPercent% off MRP (rounded to nearest PKR).
 *
 * @param {number|string} mrp               - Market Retail Price
 * @param {number}        [discountPercent]  - Override discount %; defaults to CASH_DISCOUNT_PERCENT
 */
export function cashPriceFromMrp(mrp, discountPercent = CASH_DISCOUNT_PERCENT) {
    const n = Number(mrp);
    const d = Number.isFinite(Number(discountPercent)) ? Number(discountPercent) : CASH_DISCOUNT_PERCENT;
    if (!Number.isFinite(n) || n < 0) return 0;
    if (d <= 0) return Math.round(n);
    return Math.round((n * (100 - d)) / 100);
}

/**
 * Resolves the unit cash price directly from a product object.
 *
 * @param {object} product
 * @param {number} [fallbackDiscount]
 * @returns {number}
 */
export function cashPriceForProduct(product, fallbackDiscount = CASH_DISCOUNT_PERCENT) {
    if (!product) return 0;
    const mrp = Number(product.mrp ?? product.price ?? 0);
    const discount = getProductDiscount(product, fallbackDiscount);
    return cashPriceFromMrp(mrp, discount);
}

/**
 * Total cash amount for a line item.
 *
 * @param {number|string} mrp
 * @param {number|string} quantity
 * @param {number}        [discountPercent]
 */
export function totalCashForLine(mrp, quantity, discountPercent = CASH_DISCOUNT_PERCENT) {
    const u = cashPriceFromMrp(mrp, discountPercent);
    const q = Math.max(0, Number(quantity) || 0);
    return u * q;
}

export function totalMrpForLine(mrp, quantity) {
    const u = Number(mrp);
    const q = Math.max(0, Number(quantity) || 0);
    if (!Number.isFinite(u)) return 0;
    return Math.round(u * q);
}

const PKR_NUMBER_FORMATTER = new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const toSafeRoundedNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const normalized = typeof value === 'string' ? value.replace(/,/g, '').trim() : value;
    const num = Number(normalized);
    return Number.isFinite(num) ? Math.round(num) : 0;
};

export const formatNumberPKR = (value) => {
    const rounded = toSafeRoundedNumber(value);
    return PKR_NUMBER_FORMATTER.format(Math.abs(rounded));
};

export const formatPriceDisplay = (value) => {
    const rounded = toSafeRoundedNumber(value);
    const sign = rounded < 0 ? '-' : '';
    return `${sign}Rs. ${PKR_NUMBER_FORMATTER.format(Math.abs(rounded))}`;
};

export const formatPrice = formatPriceDisplay;

